import {
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query';
import { CasinoHelper } from './CasinoHelper';
import type {
  CasinoProvider,
  CasinoCategory,
  CasinoGame,
  CasinoGamesFilter,
  CasinoGamesPage,
} from './Type/CasinoTypes';
import { queryClient } from '../Core/Query/QueryClient';

// Re-export types for convenience
export type { CasinoProvider, CasinoCategory, CasinoGame, CasinoGamesFilter, CasinoGamesPage } from './Type/CasinoTypes';

// Query Keys - centralized for cache invalidation
export const casinoQueryKeys = {
  all: ['casino'] as const,
  providers: (slotegratorOnly: boolean) => [...casinoQueryKeys.all, 'providers', { slotegratorOnly }] as const,
  categories: () => [...casinoQueryKeys.all, 'categories'] as const,
  gamesPage: (args: { filter?: CasinoGamesFilter; page: number; pageSize: number }) =>
    [...casinoQueryKeys.all, 'gamesPage', { filter: args.filter ?? null, page: args.page, pageSize: args.pageSize }] as const,
  games: (args: { filter?: CasinoGamesFilter; pageSize: number }) =>
    [...casinoQueryKeys.all, 'games', { filter: args.filter ?? null, pageSize: args.pageSize }] as const,
  gameById: (id: string) => [...casinoQueryKeys.all, 'gameById', id] as const,
  gameBySlug: (args: { slug: string; isMobile: boolean }) =>
    [...casinoQueryKeys.all, 'gameBySlug', { slug: args.slug, isMobile: args.isMobile }] as const,
};

const normalizeIdList = (value?: string[]): string[] | undefined => {
  if (!value || value.length === 0) return undefined;
  const normalized = value
    .map((id) => id.trim())
    .filter((id) => id.length > 0);
  if (normalized.length === 0) return undefined;
  return Array.from(new Set(normalized)).sort();
};

const normalizeSlug = (slug: string): string => slug.trim();

const buildSearchTitleFromSlug = (slug: string): string => {
  const trimmed = slug.trim();
  if (!trimmed) return '';
  const withoutMobile = trimmed.replace(/-mobile$/i, '');
  return withoutMobile.replace(/[-_]+/g, ' ').trim();
};

const normalizeSlugList = (value?: string[]): string[] | undefined => {
  if (!value || value.length === 0) return undefined;
  const normalized = value
    .map((slug) => slug.trim())
    .filter((slug) => slug.length > 0);
  if (normalized.length === 0) return undefined;
  return Array.from(new Set(normalized)).sort();
};

const normalizeCasinoGamesFilter = (filter?: CasinoGamesFilter): CasinoGamesFilter | undefined => {
  const status = filter?.status?.trim() || 'Active';
  const mainProvider = filter?.mainProvider?.trim();
  const searchTitle = filter?.searchTitle?.trim();
  const providerIds = normalizeIdList(filter?.providerIds);
  const categoryIds = normalizeIdList(filter?.categoryIds);
  const tagSlugs = normalizeSlugList(filter?.tagSlugs);

  const normalized: CasinoGamesFilter = {};
  if (status && status.length > 0) normalized.status = status;
  if (mainProvider && mainProvider.length > 0) normalized.mainProvider = mainProvider;
  if (typeof filter?.isMobile === 'boolean') normalized.isMobile = filter.isMobile;
  if (searchTitle && searchTitle.length > 0) normalized.searchTitle = searchTitle;
  if (providerIds && providerIds.length > 0) normalized.providerIds = providerIds;
  if (categoryIds && categoryIds.length > 0) normalized.categoryIds = categoryIds;
  if (tagSlugs && tagSlugs.length > 0) normalized.tagSlugs = tagSlugs;

  return Object.keys(normalized).length > 0 ? normalized : undefined;
};

const cacheCasinoGame = (game: CasinoGame): void => {
  if (!game?.id) return;

  queryClient.setQueryData(casinoQueryKeys.gameById(game.id), game);

  const normalizedSlug = normalizeSlug(game.slug);
  if (!normalizedSlug) return;

  if (game.mobileClient === true) {
    queryClient.setQueryData(casinoQueryKeys.gameBySlug({ slug: normalizedSlug, isMobile: true }), game);
  }
  if (game.desktopClient === true) {
    queryClient.setQueryData(casinoQueryKeys.gameBySlug({ slug: normalizedSlug, isMobile: false }), game);
  }
};

const cacheCasinoGames = (games: CasinoGame[]): void => {
  games.forEach(cacheCasinoGame);
};

export const getCachedCasinoGameBySlug = (slug: string, isMobile: boolean): CasinoGame | null => {
  const normalized = normalizeSlug(slug);
  if (!normalized) return null;
  return queryClient.getQueryData(casinoQueryKeys.gameBySlug({ slug: normalized, isMobile })) ?? null;
};

export const getCachedCasinoGameById = (id: string): CasinoGame | null => {
  const normalized = id.trim();
  if (!normalized) return null;
  return queryClient.getQueryData(casinoQueryKeys.gameById(normalized)) ?? null;
};

export const providersQueryOptions = (slotegratorOnly: boolean) =>
  queryOptions({
    queryKey: casinoQueryKeys.providers(slotegratorOnly),
    queryFn: ({ signal }) => CasinoHelper.getProviders({ slotegratorOnly, signal }),
  });

export const categoriesQueryOptions = () =>
  queryOptions({
    queryKey: casinoQueryKeys.categories(),
    queryFn: ({ signal }) => CasinoHelper.getCategories({ signal }),
  });

export const casinoGameBySlugQueryOptions = (args: { slug: string; isMobile: boolean; titleHint?: string }) => {
  const normalizedSlug = normalizeSlug(args.slug);

  return queryOptions({
    queryKey: casinoQueryKeys.gameBySlug({ slug: normalizedSlug, isMobile: args.isMobile }),
    queryFn: async ({ signal }) => {
      const slug = normalizeSlug(args.slug);
      if (!slug) return null;

      const titleHint = args.titleHint?.trim();
      const derivedSearchTitle = buildSearchTitleFromSlug(slug);
      const searchTitles = Array.from(new Set([titleHint, derivedSearchTitle].filter(Boolean))) as string[];

      for (const searchTitle of searchTitles) {
        const res = await CasinoHelper.getGames({
          page: 1,
          pageSize: 80,
          filter: {
            status: 'Active',
            mainProvider: 'slotegrator',
            isMobile: args.isMobile,
            searchTitle,
          },
          signal,
        });

        const items = res?.items ?? [];
        if (items.length > 0) cacheCasinoGames(items);

        const found = items.find((g) => g.slug === slug) ?? null;
        if (found) return found;
      }

      return null;
    },
  });
};

export function useCasinoGameBySlug(
  slug: string,
  isMobile: boolean,
  options?: { enabled?: boolean; titleHint?: string }
): UseQueryResult<CasinoGame | null, Error> {
  const normalized = normalizeSlug(slug);

  return useQuery({
    ...casinoGameBySlugQueryOptions({ slug: normalized, isMobile, titleHint: options?.titleHint }),
    enabled: (options?.enabled ?? true) && normalized.length > 0,
  });
}

export const casinoGamesInfiniteQueryOptions = (filter: CasinoGamesFilter | undefined, pageSize: number) => {
  const normalizedFilter = normalizeCasinoGamesFilter(filter);

  return infiniteQueryOptions({
    queryKey: casinoQueryKeys.games({ filter: normalizedFilter, pageSize }),
    queryFn: async ({ pageParam, signal }) => {
      const res = await CasinoHelper.getGames({ page: pageParam, pageSize, filter: normalizedFilter, signal });
      if (res?.items?.length) {
        cacheCasinoGames(res.items);
      }
      return res;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const nextPage = lastPage.page + 1;
      const effectivePageSize =
        Number.isFinite(lastPage.pageSize) && lastPage.pageSize > 0 ? lastPage.pageSize : pageSize;
      const totalPages = effectivePageSize > 0 ? Math.ceil(lastPage.total / effectivePageSize) : 0;
      return nextPage <= totalPages ? nextPage : undefined;
    },
  });
};

export const casinoGamesPageQueryOptions = (args: {
  filter?: CasinoGamesFilter;
  page?: number;
  pageSize: number;
}) => {
  const normalizedFilter = normalizeCasinoGamesFilter(args.filter);
  const page = Number.isFinite(args.page) && (args.page ?? 0) > 0 ? Number(args.page) : 1;

  return queryOptions({
    queryKey: casinoQueryKeys.gamesPage({ filter: normalizedFilter, page, pageSize: args.pageSize }),
    queryFn: async ({ signal }) => {
      const res = await CasinoHelper.getGames({ page, pageSize: args.pageSize, filter: normalizedFilter, signal });
      if (res?.items?.length) {
        cacheCasinoGames(res.items);
      }
      return res;
    },
  });
};

/**
 * Hook to fetch casino providers with automatic caching and deduplication.
 * Uses CasinoHelper under the hood for GraphQL requests.
 *
 * @param slotegratorOnly - Filter to slotegrator providers only (default: true)
 * @param options - Optional configuration { enabled: boolean }
 * @returns UseQueryResult with providers data
 */
export function useCasinoProviders(
  slotegratorOnly: boolean = true,
  options?: { enabled?: boolean }
): UseQueryResult<CasinoProvider[], Error> {
  return useQuery({
    ...providersQueryOptions(slotegratorOnly),
    enabled: options?.enabled ?? true,
  });
}

/**
 * Hook to fetch casino categories with automatic caching and deduplication.
 * Uses CasinoHelper under the hood for GraphQL requests.
 *
 * @param options - Optional configuration { enabled: boolean }
 * @returns UseQueryResult with categories data
 */
export function useCasinoCategories(
  options?: { enabled?: boolean }
): UseQueryResult<CasinoCategory[], Error> {
  return useQuery({
    ...categoriesQueryOptions(),
    enabled: options?.enabled ?? true,
  });
}

/**
 * Hook to fetch casino games with infinite scroll support.
 * Uses CasinoHelper under the hood for GraphQL requests.
 *
 * @param filter - Filter criteria for games
 * @param pageSize - Number of games per page (default: 40)
 * @param options - Optional configuration { enabled: boolean }
 * @returns Infinite query result with additional helpers (allGames, total, hasMore)
 */
export function useCasinoGames(
  filter?: CasinoGamesFilter,
  pageSize: number = 40,
  options?: { enabled?: boolean }
) {
  const query = useInfiniteQuery({
    ...casinoGamesInfiniteQueryOptions(filter, pageSize),
    enabled: options?.enabled ?? true,
  });

  // Flatten all pages into a single array of games
  const allGames = query.data?.pages.flatMap((page) => page.items ?? []) ?? [];
  const total = query.data?.pages[0]?.total ?? 0;
  const hasMore = allGames.length < total;

  return {
    ...query,
    allGames,
    total,
    hasMore,
  };
}

export function useCasinoGamesPage(
  filter?: CasinoGamesFilter,
  page: number = 1,
  pageSize: number = 40,
  options?: { enabled?: boolean }
): UseQueryResult<CasinoGamesPage, Error> {
  return useQuery({
    ...casinoGamesPageQueryOptions({ filter, page, pageSize }),
    enabled: options?.enabled ?? true,
  });
}

// Helper functions to create lookup maps from arrays
export function createProvidersMap(providers: CasinoProvider[]): Map<string, CasinoProvider> {
  return new Map(providers.map((p) => [p.id, p]));
}

export function createCategoriesMap(categories: CasinoCategory[]): Map<string, CasinoCategory> {
  return new Map(categories.map((c) => [c.id, c]));
}
